import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FetchUsers } from '../../../api/General/account';
import { SendOnDemandNotification } from "../../../api/General/Notification";
import { OnDemandNotificationInput, Result, UserView } from "../../../models/general";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { useToast, ToastRenderer } from '../../../hooks/useToast';

interface RootState {
  user: {
    userId?: any;
    company?: any;
    companyPrimaryColor?: string;
    companyName?: string;
  };
}

const OnDemandNotifications: React.FC = () => {
  const { t } = useTranslation();
  const { company, companyPrimaryColor } = useSelector((s: RootState) => s.user);
  const { toast, showToast, dismissToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserView[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserView[]>([]);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState<OnDemandNotificationInput>({ title: "", body: "", to: [], sendEmail: false } as OnDemandNotificationInput);
  const [errors, setErrors] = useState<{ title?: string; body?: string; to?: string }>({});
  const [imageFile, setImageFile] = useState<File | undefined>();
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Fetch users
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { payload } = await FetchUsers('Company', company?.id ?? 0, false);
      setUsers(payload || []);
      setFilteredUsers(payload || []);
    } catch (e) {
      showToast(t("Failed to load users"), 'error');
    } finally {
      setLoading(false);
    }
  }, [company?.id, t]);

  useEffect(() => { load(); }, [load]);

  // Filter users
  useEffect(() => {
    if (!search) { setFilteredUsers(users); return; }
    const q = search.toLowerCase();
    setFilteredUsers(users.filter(u => (u.fullName || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)));
  }, [search, users]);

  const allSelected = useMemo(() => filteredUsers.length > 0 && filteredUsers.every(u => input.to?.includes(String(u.id))), [filteredUsers, input.to]);
  const indeterminate = useMemo(() => !allSelected && (filteredUsers.some(u => input.to?.includes(String(u.id)))), [allSelected, filteredUsers, input.to]);

  const toggleSelectAll = () => {
    setInput(prev => ({ ...prev, to: allSelected ? [] : filteredUsers.map(u => String(u.id)) }));
  };

  const toggleUser = (id: string, checked: boolean) => {
    setInput(prev => ({ ...prev, to: checked ? [...(prev.to || []), id] : (prev.to || []).filter(x => x !== id) }));
  };

  const validate = (): boolean => {
    const errs: { title?: string; body?: string; to?: string } = {};
    if (!input.title?.trim()) errs.title = t("Title is required");
    if (!input.body?.trim()) errs.body = t("Body is required");
    if (!input.to || input.to.length === 0) errs.to = t("Select at least one employee");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) { return; }
    setLoading(true);
    try {
      const payload: OnDemandNotificationInput = { ...input, image: imageFile } as OnDemandNotificationInput;
      await SendOnDemandNotification(payload);
      showToast(t("Notification sent"), 'success');
      // Reset
      setInput({ title: "", body: "", to: [], sendEmail: false } as OnDemandNotificationInput);
      setImageFile(undefined);
      if (fileRef.current) fileRef.current.value = "";
    } catch (error: any) {
      let msg = t("Failed to send notification");
      if (error && error.errors && error.errors[0]) {
        const res = error as Result<never>;
        msg = res.errors[0].description;
      }
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = companyPrimaryColor || "#304EA1";

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex justify-between items-center w-full">
        <h1 className="text-xl font-semibold" style={{ color: primaryColor }}>{t("Send Notification")}</h1>
        <div className="flex items-center gap-2 text-sm text-muted">
          {!!input.to?.length && <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">{`${input.to.length} ${t("selected")}`}</span>}
        </div>
      </div>
      <hr className="border-border" />
      <div className="flex gap-6 flex-row items-start">
        {/* Left: Users table */}
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="relative">
            <Input
              placeholder={t("Search employees")}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <AppIcon name="Search" size={18} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          <div className="max-h-[420px] overflow-auto">
            <Table stickyHeader className="table-standard table-outline-rows table-compact">
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={indeterminate}
                      onChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>{t("Employee Name")}</TableHead>
                  <TableHead>{t("Email")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">
                      <Spinner size="sm" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted text-sm">
                      {search ? t("No employees match your search") : t("No employees found")}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredUsers.map(u => {
                  const idStr = String(u.id);
                  const checked = !!input.to?.includes(idStr);
                  return (
                    <TableRow key={idStr} className="cursor-pointer" data-state={checked ? 'selected' : undefined} onClick={() => toggleUser(idStr, !checked)}>
                      <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Checkbox
                          onChange={(e) => toggleUser(idStr, (e.target as HTMLInputElement).checked)}
                          checked={checked}
                        />
                      </TableCell>
                      <TableCell>{u.fullName}</TableCell>
                      <TableCell>{u.email}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {errors.to && <p className="text-sm text-destructive">{errors.to}</p>}
        </div>

        {/* Right: Form */}
        <div className="flex flex-col gap-4 card flex-1 max-w-[520px]">
          <div className="flex flex-col gap-2">
            <Input
              label={t('Title')}
              value={input.title}
              onChange={e => setInput(prev => ({ ...prev, title: e.target.value }))}
              error={errors.title}
              placeholder={t("Enter title")}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('Body')}</label>
              <textarea
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                rows={4}
                value={input.body}
                onChange={e => setInput(prev => ({ ...prev, body: e.target.value }))}
                placeholder={t("Enter the body of the notification")}
              />
              {errors.body && <p className="text-sm text-destructive">{errors.body}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">{t("Image (png, jpeg, jpg)")}</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) { setImageFile(f); }
              }}
            />
            {imageFile && (
              <div className="flex items-center gap-2 text-xs text-muted">
                <span>{imageFile.name}</span>
                <Button variant="ghost" size="xs" onClick={() => { setImageFile(undefined); if (fileRef.current) fileRef.current.value=''; }}>{t("Remove")}</Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!input.sendEmail}
              onChange={(e) => setInput(prev => ({ ...prev, sendEmail: (e.target as HTMLInputElement).checked }))}
            />
            <label className="text-sm font-medium text-foreground">{t("Send email")}</label>
          </div>
          <div className="flex justify-end">
            <Button
              variant="default"
              disabled={loading}
              onClick={handleSend}
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? <Spinner size="sm" /> : t("Send")}
            </Button>
          </div>
        </div>
      </div>
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default OnDemandNotifications;
